import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { getJsonPath } from './jsonPath';

/**
 * Configuration accepted by every `api-*` widget. Flat (no nested
 * `source: { kind, ... }`) on purpose because rjsf's WidgetEditor
 * can't conditionally hide nested fields based on a sibling's value,
 * and forcing admins to scroll through a discriminated union for
 * every widget is a worse UX than "fill in the field you care about".
 *
 * Resolution rules:
 *   - `url` set + non-empty  →  fetch over the network. `inlineJson`
 *                               is ignored.
 *   - `url` empty            →  parse `inlineJson` synchronously
 *                               (loading is never true, useful for
 *                               prototyping a dashboard before the
 *                               backend exists).
 *   - both empty             →  data is `undefined`; widgets render
 *                               an "unconfigured" hint.
 */
export type UseApiDataConfig = {
  /**
   * Absolute or relative URL. The recommended form is
   *   /api/proxy/<name>/<path>
   * because Backstage's proxy plugin handles CORS + auth via
   * server-side secrets. See widget descriptions for the full story.
   *
   * Direct HTTPS URLs work too, but the target API must send
   * `Access-Control-Allow-Origin` for the request to succeed in a
   * browser — most internal APIs do NOT.
   */
  url?: string;
  /**
   * Raw JSON string. Parsed once, then memoised, so editing the
   * field in the scaffolder doesn't re-trigger a fetch. Use this
   * mode for fixed values or while iterating on a dashboard.
   */
  inlineJson?: string;
  /**
   * Dot path applied AFTER parsing. `data` returns the full body;
   * `data.items.0.value` drills in. Empty = whole body.
   */
  path?: string;
  /**
   * If set and > 0, re-fetch the URL every N seconds. Cleared on
   * unmount. Has no effect in inline-JSON mode (the data never
   * changes), so it's safe for the admin to leave a non-zero value
   * even if they're prototyping.
   */
  refreshSeconds?: number;
};

export type UseApiDataResult = {
  /** The value at `path`. Undefined while loading the first response,
   *  on error, or when `path` doesn't resolve. */
  data: unknown;
  /** The whole parsed JSON body — useful for ApiJsonViewer and for
   *  widgets that want to inspect siblings (e.g. metadata next to
   *  the extracted value). */
  raw: unknown;
  loading: boolean;
  /** Network errors AND JSON parse errors land here with their
   *  message preserved, so the widget's error banner can show
   *  "Unexpected token } in JSON at position 47" instead of a
   *  generic "something went wrong". */
  error: Error | undefined;
  /** ISO timestamp of the most recent successful fetch / parse.
   *  Drives the "last updated" stamp in chart / stat widgets. */
  lastUpdatedAt: Date | undefined;
  /** Manual trigger so a widget can expose a "Refresh now" button.
   *  Reuses the same code path as the interval — keeps both in sync. */
  refetch: () => void;
};

/* Conservative URL filter. We accept:
 *   - Relative paths starting with `/`  (most importantly /api/proxy/...)
 *   - Absolute HTTPS URLs
 *   - HTTP only for explicit localhost (dev convenience)
 * Everything else (`javascript:`, `data:`, `file:`, `ftp:`, ...) is
 * rejected so a typo'd config doesn't accidentally turn into an XSS
 * vector or a file-system probe.
 */
const isSafeUrl = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('/')) return true;
  try {
    const u = new URL(trimmed);
    if (u.protocol === 'https:') return true;
    if (
      u.protocol === 'http:' &&
      (u.hostname === 'localhost' || u.hostname === '127.0.0.1')
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * One hook to rule the `api-*` widgets. Handles:
 *
 *   - URL fetching with Backstage's `fetchApi` (auth-aware for
 *     same-origin /api/* calls)
 *   - Inline-JSON mode (no fetch, synchronous parse)
 *   - Dot-path extraction
 *   - Periodic auto-refresh
 *   - Clean teardown of in-flight requests on unmount or config
 *     change so a slow API never resolves into stale state
 *
 * We use `fetchApi` instead of the global `fetch` for ONE specific
 * reason: it transparently attaches the user's session token on
 * same-origin requests (`/api/proxy/...`, `/api/auth/...`, etc.),
 * which is the whole point of routing API calls through Backstage's
 * proxy in the first place.
 */
export const useApiData = (config: UseApiDataConfig): UseApiDataResult => {
  const { fetch } = useApi(fetchApiRef);

  // Stable signature for the effect dependency array. We don't want
  // a re-render of the parent (which can re-run the widget render
  // function and create a brand-new `config` object reference) to
  // trigger a fresh fetch.
  const signature = `${config.url ?? ''}::${config.inlineJson ?? ''}::${
    config.path ?? ''
  }::${config.refreshSeconds ?? 0}`;

  const [raw, setRaw] = useState<unknown>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | undefined>(
    undefined,
  );

  /* Track the latest "fetch generation" so an in-flight request that
   * resolves AFTER the admin edits the URL field doesn't overwrite
   * the new state with stale data. Bumped on every effect run; only
   * the generation that matches at resolve time gets to call setState. */
  const generationRef = useRef(0);

  useEffect(() => {
    const myGeneration = ++generationRef.current;

    // ---- Inline JSON mode ------------------------------------------
    if (!config.url || !config.url.trim()) {
      if (!config.inlineJson || !config.inlineJson.trim()) {
        setRaw(undefined);
        setLoading(false);
        setError(undefined);
        setLastUpdatedAt(undefined);
        return;
      }
      try {
        const parsed = JSON.parse(config.inlineJson);
        if (myGeneration === generationRef.current) {
          setRaw(parsed);
          setLoading(false);
          setError(undefined);
          setLastUpdatedAt(new Date());
        }
      } catch (e) {
        if (myGeneration === generationRef.current) {
          setRaw(undefined);
          setLoading(false);
          setError(
            new Error(
              `Invalid inline JSON: ${(e as Error).message}. Tip: paste the response from your API directly — no surrounding quotes.`,
            ),
          );
        }
      }
      return;
    }

    // ---- URL fetch mode --------------------------------------------
    if (!isSafeUrl(config.url)) {
      setRaw(undefined);
      setLoading(false);
      setError(
        new Error(
          `Refusing to fetch "${config.url}". Allowed: relative paths starting with "/" (e.g. /api/proxy/<name>/<path>) or absolute HTTPS URLs. Use Backstage's proxy plugin for auth + CORS.`,
        ),
      );
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      setLoading(true);
      try {
        const response = await fetch(config.url!, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status} ${response.statusText} — ${config.url}`,
          );
        }
        const body = await response.json();
        if (cancelled || myGeneration !== generationRef.current) return;
        setRaw(body);
        setError(undefined);
        setLastUpdatedAt(new Date());
      } catch (e) {
        if (cancelled || myGeneration !== generationRef.current) return;
        setError(e as Error);
        // Deliberately keep `raw` from the previous successful fetch
        // so a transient failure doesn't blank the dashboard — the
        // error banner is enough signal that data is stale.
      } finally {
        if (!cancelled && myGeneration === generationRef.current) {
          setLoading(false);
        }
      }
    };

    doFetch();

    // Auto-refresh: only set up the interval AFTER the first fetch
    // kicks off, otherwise an interval of e.g. 5s would always be 5s
    // behind the user typing in the URL field.
    let intervalHandle: ReturnType<typeof setInterval> | undefined;
    if (
      typeof config.refreshSeconds === 'number' &&
      config.refreshSeconds > 0
    ) {
      // Clamp the lower bound to avoid an admin typing `1` and DoS'ing
      // their own API by accident — anything below 5s is almost
      // certainly a typo. Upper bound is open: a 24h refresh is fine
      // for slowly-changing metrics.
      const seconds = Math.max(5, config.refreshSeconds);
      intervalHandle = setInterval(doFetch, seconds * 1000);
    }

    return () => {
      cancelled = true;
      if (intervalHandle) clearInterval(intervalHandle);
    };
    // We intentionally depend on `signature` (a derived string)
    // instead of each field — see comment above on the `signature`
    // useMemo for the reasoning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, fetch]);

  const data = useMemo(
    () => getJsonPath(raw, config.path),
    [raw, config.path],
  );

  const refetch = () => {
    // Re-running the effect by bumping the dep would be heavy-handed
    // (it'd also reset error state, etc.). Instead we bump the
    // generation and re-trigger via a state nudge — `setRaw(prev =>
    // prev)` would no-op, so we re-run the effect via a sentinel.
    generationRef.current++;
    // The simplest way to retrigger the effect is to flip a unique
    // ref-tracked counter. But useState would also cause a re-render
    // even when state is the same. We achieve the cleanest behavior
    // by just calling setLoading(true) — the effect itself is
    // signature-keyed so we don't re-enter it, but for manual refetch
    // we explicitly call the same fetch path here.
    if (config.url && isSafeUrl(config.url)) {
      setLoading(true);
      fetch(config.url, { headers: { Accept: 'application/json' } })
        .then(async response => {
          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status} ${response.statusText} — ${config.url}`,
            );
          }
          return response.json();
        })
        .then(body => {
          setRaw(body);
          setError(undefined);
          setLastUpdatedAt(new Date());
        })
        .catch(e => setError(e as Error))
        .finally(() => setLoading(false));
    }
  };

  return { data, raw, loading, error, lastUpdatedAt, refetch };
};
